'use client';

import { AccessibleHiddenText } from '@cc-shopping/ui/primitives/AccessibleHiddenText';
import { Box, List, ListItem } from '@mui/material';
import type { ReactNode } from 'react';
import { Fragment, useId, useState } from 'react';

const SHARED_NAVIGATION_BUTTON_STYLES = {
  height: '56px',
  backgroundColor: 'greens.lighter',
  borderRadius: 0,
  color: 'greens.dark',
  cursor: 'pointer',
  display: 'none',
  position: 'absolute',
  width: 'var(--chevron-width)',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    bgcolor: '#02b27387',
  },
};

export const CreditCardsSlideshowShell = ({
  slideContents,
  slideImages,
}: {
  slideContents: ReactNode[];
  slideImages: ReactNode[];
}) => {
  const id = useId();
  const [activeSlide, setActiveSlide] = useState(0);

  const totalSlides = slideContents.length;

  const handleNext = () => {
    setActiveSlide(prev => (prev >= totalSlides - 1 ? totalSlides - 1 : prev + 1));
  };

  const handleBack = () => {
    setActiveSlide(prev => (prev <= 0 ? 0 : prev - 1));
  };

  const handleJump = (targetSlide: number) => {
    setActiveSlide(targetSlide);
  };

  return (
    <Box
      id={id}
      sx={{
        display: 'flex',
        bgcolor: 'greens.lightest',
        borderRadius: 4,
        paddingY: '32px',
        marginY: 3,
        minHeight: '300px',
        alignItems: { xs: 'flex-start', sm: 'center' },
        position: 'relative',
        overflow: 'hidden',
        '--chevron-width': '40px',
      }}
    >
      <Box sx={{ display: 'none', visibility: 'hidden' }}>
        {slideContents.map((_, index) => {
          const slideId = `${id}-${index}`;
          return (
            <Box
              key={`${slideId}-carousel-input`}
              component="input"
              type="radio"
              name={`${id}-carousel`}
              id={`${slideId}-carousel-radio`}
              defaultChecked={index === 0}
              aria-hidden="true"
              aria-controls={`${slideId}-carousel-content`}
            />
          );
        })}
      </Box>

      <List
        sx={{
          alignSelf: 'center',
          position: 'absolute',
          display: { xs: 'block', sm: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          top: { xs: '250px', sm: 'unset' },
          left: 0,
          width: '100%',
          padding: 0,
        }}
      >
        {slideContents.map((_, index) => {
          const slideId = `${id}-${index}`;
          const isShown = index === activeSlide;
          return (
            <Fragment key={slideId}>
              {index > 0 && (
                <ListItem sx={{ padding: 0 }}>
                  <Box
                    data-testid="ChooseBCCpreviousSlide"
                    aria-label="Previous slide"
                    component="label"
                    htmlFor={`${id}-${index - 1}-carousel-radio`}
                    id={`${id}-${index - 1}-carousel-arrow-left`}
                    aria-controls={`${id}-${index - 1}-carousel-content`}
                    tabIndex={0}
                    aria-hidden={!isShown}
                    sx={{
                      ...SHARED_NAVIGATION_BUTTON_STYLES,
                      borderTopRightRadius: '50%',
                      borderBottomRightRadius: '50%',
                      [`#${id}:has(input#${slideId}-carousel-radio:checked) &`]: {
                        display: 'flex',
                      },
                      left: 0,
                    }}
                    onClick={handleBack}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.currentTarget.click();
                      }
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="mask-icon mask-icon-keyboard-arrow-left"
                      style={{ width: '24px', height: '24px' }}
                    />
                  </Box>
                </ListItem>
              )}
              {index < totalSlides - 1 && (
                <ListItem sx={{ padding: 0 }}>
                  <Box
                    data-testid="ChooseBCCnextSlide"
                    aria-label="Next slide"
                    component="label"
                    aria-hidden={!isShown}
                    htmlFor={`${id}-${index + 1}-carousel-radio`}
                    id={`${id}-${index + 1}-carousel-arrow-right`}
                    aria-controls={`${id}-${index + 1}-carousel-content`}
                    tabIndex={0}
                    sx={{
                      ...SHARED_NAVIGATION_BUTTON_STYLES,
                      borderTopLeftRadius: '50%',
                      borderBottomLeftRadius: '50%',
                      [`#${id}:has(input#${slideId}-carousel-radio:checked) &`]: {
                        display: 'flex',
                      },
                      right: 0,
                    }}
                    onClick={handleNext}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.currentTarget.click();
                      }
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="mask-icon mask-icon-keyboard-arrow-right"
                      style={{ width: '24px', height: '24px' }}
                    />
                  </Box>
                </ListItem>
              )}
            </Fragment>
          );
        })}
      </List>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '64px',
          marginX: 'var(--chevron-width)',
          padding: 0,
        }}
      >
        <List sx={{ padding: 0 }}>
          {slideContents.map((content, index) => {
            const slideId = `${id}-${index}`;
            return (
              <ListItem
                key={`${slideId}-content`}
                id={`${slideId}-carousel-content`}
                aria-labelledby={`${slideId}-carousel-slide`}
                aria-hidden={index !== activeSlide}
                sx={{
                  flexDirection: { xs: 'column-reverse', sm: 'row' },
                  paddingY: 0,
                  paddingX: { xs: 3, sm: 3 },
                  flexGrow: 1,
                  gap: '32px',
                  alignItems: 'center',
                  display: 'none',
                  opacity: 0,
                  position: 'absolute',
                  height: 0,
                  color: 'transparent',
                  '& .slideshow-image': {
                    height: 0,
                    width: 0,
                  },
                  [`#${id}:has(input#${slideId}-carousel-radio:checked) &`]: {
                    '& .slideshow-image': {
                      height: 180,
                      width: { xs: 196, sm: 300 },
                    },
                    color: 'unset',
                    height: 'fit-content',
                    display: 'flex',
                    opacity: 1,
                    position: 'static',
                  },
                  transition:
                    'opacity .3s ease-in, opacity .2s ease-out, height .3s ease-in-out, position .3s ease, display .3s ease-in-out allow-discrete',
                  '@starting-style': {
                    [`#${id}:has(input#${slideId}-carousel-radio:checked) &`]: {
                      opacity: 0,
                    },
                  },
                }}
              >
                {content}
                {slideImages[index]}
              </ListItem>
            );
          })}
        </List>
        <List
          sx={{
            display: 'flex',
            width: { xs: '200px', sm: '384px' },
            height: '4px',
            alignSelf: 'center',
            padding: 0,
          }}
        >
          {Array.from({ length: totalSlides }, (_, index) => {
            const slideId = `${id}-${index}`;
            return (
              <ListItem
                key={`slider-${index}`}
                sx={{
                  flex: 1,
                  padding: 0,
                }}
              >
                <Box
                  aria-label={`Go to slide ${index + 1}`}
                  component="label"
                  htmlFor={`${slideId}-carousel-radio`}
                  id={`${slideId}-carousel-slide`}
                  aria-controls={`${slideId}-carousel-content`}
                  tabIndex={0}
                  sx={{
                    height: '100%',
                    width: '100%',
                    cursor: 'pointer',
                    bgcolor: 'neutral.lighter',
                    '&:hover': {
                      bgcolor: '#02b27387',
                    },
                    [`#${id}:has(input#${slideId}-carousel-radio:checked) &`]: {
                      bgcolor: 'primary.main',
                    },
                    transition: 'background-color 0.2s ease-in, background-color 0.5s ease-out',
                  }}
                  onClick={() => handleJump(index)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.currentTarget.click();
                    }
                  }}
                >
                  <AccessibleHiddenText>
                    Item {index + 1} of {totalSlides}
                  </AccessibleHiddenText>
                  {activeSlide === index && (
                    <AccessibleHiddenText>(Current Slide)</AccessibleHiddenText>
                  )}
                </Box>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};
